import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';

/**
 * PDF Report Generator utility for analytics dashboards.
 * Produces branded, multi-section PDF reports with:
 * - Header with title, date range, generation timestamp
 * - Metric summary cards
 * - Chart snapshots (captured from DOM via html2canvas)
 * - Data tables (via jspdf-autotable)
 * - Footer with page numbers
 *
 * Usage:
 *   const report = new ReportGenerator('GPT Cost Report', { from, to });
 *   report.addMetrics([{ label: 'Total Cost', value: '$142.50' }, ...]);
 *   await report.addChartFromRef(chartRef, 'Daily Cost Trend');
 *   report.addTable(['Date', 'Cost', 'Tokens'], rows);
 *   report.download();
 */

const COLORS = {
  primary: [0, 123, 255],      // #007bff
  dark: [33, 37, 41],           // #212529
  muted: [108, 117, 125],       // #6c757d
  light: [248, 249, 250],       // #f8f9fa
  border: [222, 226, 230],      // #dee2e6
  white: [255, 255, 255],
  accent: [40, 167, 69],        // #28a745
};

const PAGE = {
  marginLeft: 20,
  marginRight: 20,
  marginTop: 20,
  marginBottom: 25,
  width: 210, // A4
  height: 297,
};

const CONTENT_WIDTH = PAGE.width - PAGE.marginLeft - PAGE.marginRight;

export class ReportGenerator {
  /**
   * @param {string} title - Report title
   * @param {{ from?: string, to?: string }} dateRange - Optional date range
   */
  constructor(title, dateRange = {}) {
    this.doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    this.title = title;
    this.dateRange = dateRange;
    this.y = PAGE.marginTop;
    this.pageCount = 1;
    this.sections = [];

    this._drawHeader();
  }

  // ── Header ──────────────────────────────────────────────────────────────────

  _drawHeader() {
    const doc = this.doc;

    // Brand bar
    doc.setFillColor(...COLORS.primary);
    doc.rect(0, 0, PAGE.width, 12, 'F');

    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.white);
    doc.text('DocuHounds Analytics', PAGE.marginLeft, 8);

    this.y = 20;

    // Report title
    doc.setTextColor(...COLORS.dark);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(this.title, PAGE.marginLeft, this.y);
    this.y += 8;

    // Date range + generated timestamp
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.muted);

    const parts = [];
    if (this.dateRange.from || this.dateRange.to) {
      const from = this.dateRange.from ? new Date(this.dateRange.from).toLocaleDateString() : '—';
      const to = this.dateRange.to ? new Date(this.dateRange.to).toLocaleDateString() : '—';
      parts.push(`Period: ${from} – ${to}`);
    }
    parts.push(`Generated: ${new Date().toLocaleString()}`);
    doc.text(parts.join('  |  '), PAGE.marginLeft, this.y);
    this.y += 4;

    // Separator line
    doc.setDrawColor(...COLORS.border);
    doc.setLineWidth(0.5);
    doc.line(PAGE.marginLeft, this.y, PAGE.width - PAGE.marginRight, this.y);
    this.y += 6;
  }

  // ── Page management ─────────────────────────────────────────────────────────

  _checkPageBreak(requiredHeight) {
    if (this.y + requiredHeight > PAGE.height - PAGE.marginBottom) {
      this._addPage();
      return true;
    }
    return false;
  }

  _addPage() {
    this._drawFooter();
    this.doc.addPage();
    this.pageCount++;
    this.y = PAGE.marginTop;
  }

  _drawFooter() {
    const doc = this.doc;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.muted);
    doc.text(
      `Page ${this.pageCount}`,
      PAGE.width / 2,
      PAGE.height - 10,
      { align: 'center' }
    );
    doc.text(
      'DocuHounds Analytics Report',
      PAGE.marginLeft,
      PAGE.height - 10
    );
  }

  // ── Section heading ─────────────────────────────────────────────────────────

  addSectionHeading(text) {
    this._checkPageBreak(12);
    const doc = this.doc;
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.dark);
    doc.text(text, PAGE.marginLeft, this.y);
    this.y += 3;
    doc.setDrawColor(...COLORS.primary);
    doc.setLineWidth(0.8);
    doc.line(PAGE.marginLeft, this.y, PAGE.marginLeft + 40, this.y);
    this.y += 6;
  }

  // ── Metric cards ────────────────────────────────────────────────────────────

  /**
   * Add a row of metric summary cards.
   * @param {{ label: string, value: string|number, sublabel?: string }[]} metrics
   */
  addMetrics(metrics) {
    if (!metrics || metrics.length === 0) return;
    this._checkPageBreak(22);

    const doc = this.doc;
    const cardCount = Math.min(metrics.length, 4);
    const gap = 4;
    const cardWidth = (CONTENT_WIDTH - (cardCount - 1) * gap) / cardCount;
    const cardHeight = 18;

    metrics.slice(0, 4).forEach((m, i) => {
      const x = PAGE.marginLeft + i * (cardWidth + gap);

      // Card background
      doc.setFillColor(...COLORS.light);
      doc.setDrawColor(...COLORS.border);
      doc.roundedRect(x, this.y, cardWidth, cardHeight, 2, 2, 'FD');

      // Value
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.dark);
      const valueStr = String(m.value);
      doc.text(valueStr, x + cardWidth / 2, this.y + 8, { align: 'center' });

      // Label
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.muted);
      doc.text(m.label, x + cardWidth / 2, this.y + 13, { align: 'center' });

      // Sublabel
      if (m.sublabel) {
        doc.setFontSize(6);
        doc.text(m.sublabel, x + cardWidth / 2, this.y + 16.5, { align: 'center' });
      }
    });

    this.y += cardHeight + 6;

    // If more than 4 metrics, add a second row
    if (metrics.length > 4) {
      this.addMetrics(metrics.slice(4));
    }
  }

  // ── Chart snapshot ──────────────────────────────────────────────────────────

  /**
   * Capture a DOM element (e.g., a chart container) as an image and add it to the PDF.
   * @param {HTMLElement|React.RefObject} elementOrRef - DOM element or React ref
   * @param {string} [caption] - Optional caption below the chart
   * @param {{ maxHeight?: number }} [options]
   */
  async addChartFromRef(elementOrRef, caption, options = {}) {
    const element = elementOrRef?.current || elementOrRef;
    if (!element) return;

    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
      });

      const imgData = canvas.toDataURL('image/png');
      const imgAspect = canvas.width / canvas.height;
      let imgWidth = CONTENT_WIDTH;
      let imgHeight = imgWidth / imgAspect;

      const maxH = options.maxHeight || 100;
      if (imgHeight > maxH) {
        imgHeight = maxH;
        imgWidth = imgHeight * imgAspect;
      }

      const totalNeeded = imgHeight + (caption ? 8 : 4);
      this._checkPageBreak(totalNeeded);

      this.doc.addImage(imgData, 'PNG', PAGE.marginLeft, this.y, imgWidth, imgHeight);
      this.y += imgHeight + 2;

      if (caption) {
        this.doc.setFontSize(8);
        this.doc.setFont('helvetica', 'italic');
        this.doc.setTextColor(...COLORS.muted);
        this.doc.text(caption, PAGE.marginLeft + CONTENT_WIDTH / 2, this.y, { align: 'center' });
        this.y += 6;
      }

      this.y += 4;
    } catch (err) {
      console.warn('[ReportGenerator] Failed to capture chart:', err);
      this._addPlaceholder('Chart could not be captured');
    }
  }

  _addPlaceholder(text) {
    this._checkPageBreak(15);
    const doc = this.doc;
    doc.setFillColor(245, 245, 245);
    doc.setDrawColor(...COLORS.border);
    doc.roundedRect(PAGE.marginLeft, this.y, CONTENT_WIDTH, 12, 2, 2, 'FD');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...COLORS.muted);
    doc.text(text, PAGE.marginLeft + CONTENT_WIDTH / 2, this.y + 7, { align: 'center' });
    this.y += 16;
  }

  // ── Data table ──────────────────────────────────────────────────────────────

  /**
   * Add a data table to the report.
   * @param {string[]} headers - Column headers
   * @param {(string|number)[][]} rows - Table data rows
   * @param {{ title?: string }} [options]
   */
  addTable(headers, rows, options = {}) {
    if (!headers || !rows || rows.length === 0) return;

    if (options.title) {
      this._checkPageBreak(10);
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'bold');
      this.doc.setTextColor(...COLORS.dark);
      this.doc.text(options.title, PAGE.marginLeft, this.y);
      this.y += 5;
    }

    this.doc.autoTable({
      head: [headers],
      body: rows.map(row => row.map(cell => String(cell ?? ''))),
      startY: this.y,
      margin: { left: PAGE.marginLeft, right: PAGE.marginRight },
      styles: {
        fontSize: 7,
        cellPadding: 2,
        lineColor: COLORS.border,
        lineWidth: 0.25,
        textColor: COLORS.dark,
      },
      headStyles: {
        fillColor: COLORS.primary,
        textColor: COLORS.white,
        fontStyle: 'bold',
        fontSize: 7.5,
      },
      alternateRowStyles: {
        fillColor: COLORS.light,
      },
      didDrawPage: () => {
        this.pageCount++;
      },
    });

    this.y = this.doc.lastAutoTable.finalY + 6;
  }

  // ── Text / notes ────────────────────────────────────────────────────────────

  /**
   * Add a paragraph of text.
   * @param {string} text
   */
  addText(text) {
    if (!text) return;
    this._checkPageBreak(10);
    const doc = this.doc;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.dark);
    const lines = doc.splitTextToSize(text, CONTENT_WIDTH);
    const lineHeight = 4;
    const totalHeight = lines.length * lineHeight;
    this._checkPageBreak(totalHeight + 4);
    doc.text(lines, PAGE.marginLeft, this.y);
    this.y += totalHeight + 4;
  }

  // ── Spacer ──────────────────────────────────────────────────────────────────

  addSpace(mm = 6) {
    this.y += mm;
  }

  // ── Download ────────────────────────────────────────────────────────────────

  /**
   * Finalize and download the PDF.
   * @param {string} [filename] - Output filename (without .pdf extension)
   */
  download(filename) {
    this._drawFooter();
    const name = filename || this.title.replace(/[^a-zA-Z0-9_-]/g, '_').toLowerCase();
    this.doc.save(`${name}.pdf`);
  }

  /**
   * Return the PDF as a Blob (e.g., for email attachment or preview).
   * @returns {Blob}
   */
  toBlob() {
    this._drawFooter();
    return this.doc.output('blob');
  }
}

export default ReportGenerator;
